import { faSearch } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { EUsageMetrics } from '@feathr/blackbox';
import { Button, DebouncedInput, Input } from '@feathr/components';
import Page from '@feathr/extender/App/Page';
import { useLocalUrl, useUsageAndQuota } from '@feathr/extender/state';

import UsersColumns from './UsersColumns';
import UsersTable from './UsersTable';

import * as styles from './UsersPage.css';

function UsersPage(): JSX.Element {
  const localUrl = useLocalUrl();
  const [search, setSearch] = useState<string | undefined>();
  const { t } = useTranslation();
  const { accountId } = useParams<{ accountId: string }>();
  const { getUsageText } = useUsageAndQuota(accountId, [EUsageMetrics.Users]);

  function handleDebouncedSearchChange(newValue?: string): void {
    setSearch(newValue);
  }

  const filterElements = [
    undefined,
    <>
      <DebouncedInput<string> defaultValue={search} onChange={handleDebouncedSearchChange}>
        {(liveValue, onChangeLiveValue): JSX.Element => (
          <Input
            className={styles.filter}
            isClearable={true}
            key={'users-search'}
            onChange={onChangeLiveValue}
            placeholder={t('Search by name or email...')}
            prefix={<FontAwesomeIcon icon={faSearch} />}
            type={'text'}
            value={liveValue}
          />
        )}
      </DebouncedInput>
    </>,
  ];

  const filters = search
    ? {
        __raw__: {
          $or: [
            { name: { $regex: search, $options: 'i' } },
            { email: { $regex: search, $options: 'i' } },
          ],
        },
      }
    : undefined;

  return (
    <Page
      actions={[
        <Button
          key={'inviteUsers'}
          link={localUrl('/settings/account/users/invite')}
          type={'primary'}
        >
          {t('Invite users')}
        </Button>,
      ]}
      secondaryText={getUsageText()}
      title={t('Users')}
    >
      <UsersTable columns={UsersColumns} filterElements={filterElements} filters={filters} />
    </Page>
  );
}

export default UsersPage;
